import { useCallback, useMemo } from 'react';

import { type ScopedPermissions } from '@air/api';
import { type Board } from '@air/api/types';

import { type GetBoardDragItem, type RearrangableItem } from '~/components/Shared/Drag/dragTypes';
import { useBoardPermissionsCache } from '~/hooks/useBoardPermissionsCache';
import { useRearrangeBoards } from '~/swr-hooks/gallery/useGalleryRearrange';
import { useSubnavSort } from '~/swr-hooks/subnav/useSubnavSort';
import {
  canChangeBoardAssetsOrBoardsPosition,
  canChangeWorkspaceAssetsOrBoardsPosition,
} from '~/utils/permissions/boardPermissions';

export type OnBoardRearrangeParams = {
  adjacentItem: RearrangableItem;
  boards: Board[];
  parentBoardId: Board['id'] | null;
  board: GetBoardDragItem;
};

export type GetCanRearrangeParams = {
  parentBoardId: Board['id'] | null;
  board?: GetBoardDragItem;
};

export type UseWorkspaceBoardRearrangeParams = {
  workspacePermissions: ScopedPermissions | undefined;
};

export const useWorkspaceBoardRearrange = ({ workspacePermissions }: UseWorkspaceBoardRearrangeParams) => {
  const { rearrangeBoards } = useRearrangeBoards();
  const { data: subnavSort } = useSubnavSort();
  const { getBoardPermissions } = useBoardPermissionsCache();

  const onBoardRearrange = useCallback(
    ({ adjacentItem, boards, parentBoardId, board }: OnBoardRearrangeParams) => {
      const isCustomSort = subnavSort?.boardSort?.name === 'customSort';
      if (!isCustomSort) {
        return;
      }

      rearrangeBoards({
        boards,
        draggedItem: board,
        adjacentItem,
        parentBoardId,
        library: undefined,
      });
    },
    [rearrangeBoards, subnavSort],
  );

  const getCanRearrange = useCallback(
    ({ parentBoardId }: GetCanRearrangeParams) => {
      if (!workspacePermissions) {
        return false;
      }

      // Don't allow rearranging when boards are sorted alphabetically or by date
      // because manual reordering would be immediately overridden by the active sort
      const isCustomSort = subnavSort?.boardSort?.name === 'customSort';
      if (!isCustomSort) {
        return false;
      }

      if (!parentBoardId) {
        return canChangeWorkspaceAssetsOrBoardsPosition(workspacePermissions);
      }

      return canChangeBoardAssetsOrBoardsPosition(getBoardPermissions(parentBoardId));
    },
    [workspacePermissions, subnavSort, getBoardPermissions],
  );

  return useMemo(
    () => ({
      onBoardRearrange,
      getCanRearrange,
    }),
    [onBoardRearrange, getCanRearrange],
  );
};
